import type { ProductListingResolution } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { ProductThumbnail } from "@/components/shared/product-thumbnail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { SelectedProduct } from "@/lib/campaign-creation-state";
import { EligibilityCheckRetry } from "./PickerParts";
import {
  cannotAddListing,
  eligibilityExplanation,
  listingBlockedReason,
  type ProductInventoryGate,
  selectedProductFromListing,
} from "./product-listing-helpers";

/** One resolved, ambiguous or unmatched identifier from the bulk-enter popover. */
export function ResolutionRow({
  resolution,
  selectedSkus,
  addingDisabled,
  adProduct,
  inventory,
  onAdd,
  onRetry,
  retryPending,
}: {
  resolution: ProductListingResolution;
  selectedSkus: Map<string, SelectedProduct>;
  addingDisabled: boolean;
  adProduct?: "sp";
  inventory: ProductInventoryGate;
  onAdd: (product: SelectedProduct) => void;
  onRetry: () => void;
  retryPending: boolean;
}) {
  const { t } = useTranslation("campaignCreation");
  if (resolution.type === "ambiguous") {
    return (
      <div className="rounded-lg border border-line p-3">
        <p className="mb-2 text-xs font-medium">
          {resolution.identifier} · {t("products.enter.ambiguous")}
        </p>
        <div className="flex flex-col gap-1">
          {resolution.candidates.map((candidate) => (
            <div
              key={candidate.sku}
              className="flex items-center justify-between gap-3 rounded-md bg-subtle px-2 py-1.5"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <ProductThumbnail
                  imageUrl={candidate.imageUrl}
                  label={candidate.title ?? candidate.asin}
                  asin={candidate.asin}
                  sku={candidate.sku}
                  productStatus={candidate.productStatus}
                  compact
                />
                <span className="min-w-0 truncate font-mono text-[11px]">
                  {candidate.sku}
                  {listingBlockedReason(candidate, inventory) ||
                  candidate.eligibility === "eligible_with_warning" ? (
                    <span className="ml-2">
                      {eligibilityExplanation(candidate, t) ??
                        t(
                          candidate.eligibility === "eligible_with_warning"
                            ? "products.adEligibility.eligible_with_warning"
                            : `products.selectionBlocked.${listingBlockedReason(candidate, inventory)}`,
                        )}
                    </span>
                  ) : null}
                </span>
              </div>
              {adProduct === "sp" && candidate.eligibilityError === "not_returned" ? (
                <EligibilityCheckRetry pending={retryPending} onRetry={onRetry} />
              ) : null}
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={
                  addingDisabled ||
                  selectedSkus.has(candidate.sku) ||
                  cannotAddListing(candidate, adProduct, inventory)
                }
                onClick={() => onAdd(selectedProductFromListing(candidate))}
              >
                {selectedSkus.has(candidate.sku)
                  ? t("products.enter.added")
                  : t("products.enter.add")}
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (resolution.type === "resolved") {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-line p-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <ProductThumbnail
            imageUrl={resolution.listing.imageUrl}
            label={resolution.listing.title ?? resolution.listing.asin}
            asin={resolution.listing.asin}
            sku={resolution.listing.sku}
            productStatus={resolution.listing.productStatus}
            compact
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium leading-5">{resolution.identifier}</p>
            <p className="truncate font-mono text-[10px] text-muted-foreground">
              {resolution.listing.sku} · {resolution.listing.asin}
            </p>
            {adProduct === "sp" && resolution.listing.eligibilityError === "not_returned" ? (
              <EligibilityCheckRetry pending={retryPending} onRetry={onRetry} />
            ) : null}
            {listingBlockedReason(resolution.listing, inventory) ||
            resolution.listing.eligibility === "eligible_with_warning" ? (
              <p className="text-xs text-muted-foreground">
                {eligibilityExplanation(resolution.listing, t) ??
                  t(
                    resolution.listing.eligibility === "eligible_with_warning"
                      ? "products.adEligibility.eligible_with_warning"
                      : `products.selectionBlocked.${listingBlockedReason(resolution.listing, inventory)}`,
                  )}
              </p>
            ) : null}
          </div>
        </div>
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={
            addingDisabled ||
            selectedSkus.has(resolution.listing.sku) ||
            cannotAddListing(resolution.listing, adProduct, inventory)
          }
          onClick={() => onAdd(selectedProductFromListing(resolution.listing))}
        >
          {selectedSkus.has(resolution.listing.sku)
            ? t("products.enter.added")
            : t("products.enter.add")}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-line p-3">
      <span className="font-mono text-xs">{resolution.identifier}</span>
      <Badge variant="secondary">{t(`products.enter.${resolution.type}`)}</Badge>
    </div>
  );
}
